import { dateLocale } from "@/lib/dateTime";
import React, { useEffect, useState } from "react";
import { Check, Copy, Link2, Loader2, Mail, Paperclip, Send } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { cn } from "@/lib/utils";
import type { ProposalChannel, ProposalRecord, ProposalSendResult } from "@/types/proposal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fmtDate = (d?: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "";

const CHANNELS: Array<{ id: ProposalChannel; label: string; icon: React.ElementType }> = [
  { id: "EMAIL", label: "Email", icon: Mail },
  { id: "WHATSAPP", label: "WhatsApp", icon: WhatsAppIcon },
  { id: "LINK", label: "Copy link", icon: Link2 },
];

/**
 * Sends a proposal: by email or WhatsApp with a personal link, or just creates a link for the rep
 * to share. Each send gets its own link, so views show who opened it.
 */
export function SendProposalDialog({
  proposal,
  open,
  onOpenChange,
  onSent,
}: {
  proposal: ProposalRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: (result: ProposalSendResult) => void;
}) {
  const { success, error } = useToast();
  const [channel, setChannel] = useState<ProposalChannel>("EMAIL");
  const [contactId, setContactId] = useState("");
  const [contactLabel, setContactLabel] = useState("");
  const [firstName, setFirstName] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [waMessage, setWaMessage] = useState("");
  const [attachPdf, setAttachPdf] = useState(false);
  const [sending, setSending] = useState(false);
  const [createdLink, setCreatedLink] = useState("");
  const [copied, setCopied] = useState(false);

  const isResend = proposal.proposal_status === "SENT" || proposal.proposal_status === "VIEWED";
  const customer = proposal.account?.name || "your team";

  const messages = (name: string) => {
    const hello = name ? `Dear ${name},` : "Hello,";
    return {
      email: isResend
        ? `${hello}\n\nA quick reminder about our proposal "${proposal.title}". It is valid until ${fmtDate(proposal.valid_until)}.\n\nHappy to answer any questions or walk you through it.`
        : `${hello}\n\nThank you for your time. Please find our proposal "${proposal.title}" for ${customer}. It is valid until ${fmtDate(proposal.valid_until)}.\n\nI'd be happy to walk you through it.`,
      whatsapp: `${hello} here is our proposal "${proposal.title}", valid until ${fmtDate(proposal.valid_until)}.`,
    };
  };

  useEffect(() => {
    if (!open) return;
    const first = proposal.contact?.name?.split(" ")[0] || "";
    setChannel("EMAIL");
    setContactId(proposal.contact_id || "");
    setContactLabel(proposal.contact?.name || "");
    setFirstName(first);
    setTo(proposal.contact?.email || "");
    setCc("");
    setSubject(isResend ? `Reminder: ${proposal.title}` : `Proposal: ${proposal.title}`);
    const m = messages(first);
    setMessage(m.email);
    setWaMessage(m.whatsapp);
    setAttachPdf(false);
    setCreatedLink("");
    setCopied(false);
  }, [open, proposal.id]);

  const pickContact = (id: string, row: any) => {
    setContactId(id);
    const name = row ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : "";
    setContactLabel(name);
    if (row?.email) setTo(row.email);
    // Re-greet only if the greeting wasn't edited
    const before = messages(firstName);
    const after = messages(row?.first_name || "");
    if (message === before.email) setMessage(after.email);
    if (waMessage === before.whatsapp) setWaMessage(after.whatsapp);
    setFirstName(row?.first_name || "");
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, any> = { channel, contact_id: contactId || undefined };
    if (channel === "EMAIL") {
      const ccList = cc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
      if (!EMAIL_RE.test(to.trim())) return error("Check the email", "Enter a valid email address for the customer");
      const badCc = ccList.find((c) => !EMAIL_RE.test(c));
      if (badCc) return error("Check the email", `CC address "${badCc}" isn't valid`);
      Object.assign(body, { to: to.trim(), cc: ccList.length ? ccList : undefined, subject: subject.trim() || undefined, message: message.trim() || undefined, attach_pdf: attachPdf });
    } else if (channel === "WHATSAPP") {
      if (!contactId) return error("Choose a contact", "Pick who gets the WhatsApp message");
      body.message = waMessage.trim() || undefined;
    }
    setSending(true);
    try {
      const res = unwrapObject<ProposalSendResult>(await api.post(`/api/Proposal/Send/${proposal.id}`, body));
      const stage = res.advanced_to ? ` The deal moved to ${res.advanced_to}.` : res.stage_note ? ` ${res.stage_note}` : "";
      if (channel === "LINK") {
        setCreatedLink(res.url);
        success("Link ready", `Copy it and share it with ${contactLabel || "the customer"}.${stage}`);
      } else {
        success(isResend ? "Sent again" : "Proposal sent", `Sent ${res.via} to ${res.sent_to}.${stage}`);
        onOpenChange(false);
      }
      onSent(res);
    } catch (err: any) {
      error("Not sent", err.message || "Could not send the proposal");
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(createdLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      error("Couldn't copy", "Select the link and copy it");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Send className="size-5 text-primary" />
            {isResend ? "Send again" : "Send proposal"} · {proposal.display_number}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            The customer gets a personal link to read it online and download the PDF; you'll see when they open it.
            {!isResend && " Sending freezes the content: later changes need a new version."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-1 rounded-xl border bg-muted/40 p-1 text-xs font-semibold">
          {CHANNELS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => (setChannel(c.id), setCreatedLink(""))}
              className={cn(
                "h-8 rounded-lg inline-flex items-center justify-center gap-1.5 transition-colors",
                channel === c.id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <c.icon className="size-3.5" color="currentColor" /> {c.label}
            </button>
          ))}
        </div>

        <form onSubmit={send} className="space-y-3.5 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs sm:text-sm font-semibold">{channel === "LINK" ? "Link for" : "Contact"}</Label>
            <SearchSelect
              endpoint="/api/Contact/List"
              extraParams={{ account_id: proposal.account_id || undefined }}
              value={contactId}
              selectedLabel={contactLabel}
              getLabel={(row) =>
                `${row.first_name || ""} ${row.last_name || ""}`.trim() +
                (channel === "WHATSAPP" ? (row.mobile || row.phone ? ` (${row.mobile || row.phone})` : " (no mobile)") : row.email ? ` (${row.email})` : "")
              }
              placeholder="Search the account's contacts"
              allowEmpty={channel !== "WHATSAPP"}
              emptyLabel="-- Not a saved contact --"
              onChange={pickContact}
            />
          </div>

          {channel === "EMAIL" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-semibold">To *</Label>
                  <Input type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@company.com" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-semibold">CC</Label>
                  <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Comma-separated" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Subject</Label>
                <Input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Message</Label>
                <Textarea rows={6} maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} />
                <p className="text-[11px] text-muted-foreground">A "View the proposal" button with the link is added below your message.</p>
              </div>
              <label className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground rounded-md bg-muted/40 px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} className="size-4 accent-primary" />
                <Paperclip className="size-4" /> Also attach {proposal.display_number}.pdf
              </label>
            </>
          )}

          {channel === "WHATSAPP" && (
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Message</Label>
              <Textarea rows={4} maxLength={1000} value={waMessage} onChange={(e) => setWaMessage(e.target.value)} />
              <p className="text-[11px] text-muted-foreground">
                The link is added at the end. If the customer hasn't messaged you in the last 24 hours, WhatsApp only allows an approved
                template, so the "Proposal sent" template set by your WhatsApp admin is used instead of this text.
              </p>
            </div>
          )}

          {channel === "LINK" && (
            <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground space-y-2">
              {createdLink ? (
                <>
                  <div className="flex items-center gap-2">
                    <input readOnly value={createdLink} onFocus={(e) => e.target.select()} className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-xs font-mono text-foreground" />
                    <Button type="button" size="sm" variant="outline" onClick={copy} className="gap-1.5">
                      {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <p>Paste it into your own email or chat. Views through it are tracked for {contactLabel || "this link"}.</p>
                </>
              ) : (
                <p>
                  Creates a personal link you can paste anywhere. Make one link per person, so you can see who opened it and switch
                  it off later if needed.
                </p>
              )}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {createdLink ? "Done" : "Cancel"}
            </Button>
            {!(channel === "LINK" && createdLink) && (
              <Button type="submit" disabled={sending} className="gap-1.5">
                {sending ? <Loader2 className="size-4 animate-spin" /> : channel === "LINK" ? <Link2 className="size-4" /> : <Send className="size-4" />}
                {channel === "LINK" ? "Create link" : channel === "WHATSAPP" ? "Send on WhatsApp" : "Send email"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
